export type TransactionProgressStatus =
  | "connecting"
  | "idle"
  | "queued"
  | "running"
  | "processing"
  | "completed"
  | "completed_with_errors"
  | "stopped"
  | "failed"

export type TransactionProgress = {
  id: string
  type: string
  organisationId?: number
  status: TransactionProgressStatus | string
  message: string
  progress: number
  result?: number | null
  title?: string | null
  updatedBy?: string | null
  initiatedBy?: string | null
  initiatedByEmail?: string | null
  initiatedAt?: string
  updatedAt: string
  startedAt?: string
  terminalAt?: string
  originPath?: string
  destinationPath?: string
}

export type TrackTransactionInput = {
  id: string | number
  type: string
  title: string
  message: string
  destinationPath?: string
}

export const normalizeTransactionType = (type: string) =>
  type.trim().toLowerCase()

export const getTransactionKey = (type: string, id: string | number) =>
  `${normalizeTransactionType(type)}:${id}`

export const activeTransactionStatuses = new Set([
  "connecting",
  "queued",
  "running",
  "processing",
])

export const terminalTransactionStatuses = new Set([
  "completed",
  "completed_with_errors",
  "stopped",
  "failed",
])

export const isActiveTransaction = (status?: string) =>
  activeTransactionStatuses.has(status?.toLowerCase() || "")

export const isTerminalTransaction = (status?: string) =>
  terminalTransactionStatuses.has(status?.toLowerCase() || "")

export const getProcessActivityTime = (process: TransactionProgress) =>
  isTerminalTransaction(process.status) && process.terminalAt
    ? process.terminalAt
    : process.updatedAt

export const RECENT_PROCESS_TTL_MS = 60 * 60 * 1000

export const isExpiredRecentProcess = (
  process: TransactionProgress,
  now = Date.now()
) => {
  if (!isTerminalTransaction(process.status)) return false

  const terminalTime = new Date(
    process.terminalAt || process.updatedAt
  ).getTime()

  return Number.isFinite(terminalTime) && now - terminalTime >= RECENT_PROCESS_TTL_MS
}

export const splitProgressMessage = (message?: string) => {
  const fallbackMessage =
    message?.trim() || "Waiting for the next progress update"
  const countMatch = fallbackMessage.match(
    /^\s*(\(\s*\d+\s*\/\s*\d+\s*\)|\d+\s*\/\s*\d+)\s*(?:[•·|-]\s*)?/
  )

  if (!countMatch) {
    return { count: undefined, message: fallbackMessage }
  }

  const rawCount = countMatch[1].replace(/[()\s]/g, "")
  return {
    count: `(${rawCount})`,
    message:
      fallbackMessage.slice(countMatch[0].length).trim() || fallbackMessage,
  }
}

export const getDisplayedProgress = (process?: TransactionProgress) => {
  if (!process) return 0

  const status = process.status?.toLowerCase()
  const progress = Math.min(
    Math.max(Math.round(Number(process.progress) || 0), 0),
    100
  )

  if (status === "completed") return 100

  const { count } = splitProgressMessage(process.message)

  if (isActiveTransaction(status) && count && progress === 0) {
    return 1
  }

  return progress
}

export const hasReportedProgress = (process?: TransactionProgress) =>
  Number(process?.progress) > 0

export const shouldDisplayProgressPercentage = (status?: string) =>
  !["failed", "stopped", "completed_with_errors"].includes(
    status?.toLowerCase() || ""
  )
